import React, { useEffect, useState } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import api from '../../utils/api';

interface AmazonSearchPayload {
  _id: string;
  keyword: string;
  merchant: string;
  brand: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const AmazonSearchPayloadManagement: React.FC = () => {
  const [payloads, setPayloads] = useState<AmazonSearchPayload[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPayload, setSelectedPayload] = useState<AmazonSearchPayload | null>(null);
  const [formData, setFormData] = useState({
    keyword: '',
    merchant: '',
    brand: '',
    isActive: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPayloads();
  }, []);

  const fetchPayloads = async () => {
    try {
      setLoading(true);
      const response = await api.get('/amazon-search-payloads');
      if (response.data.success) {
        setPayloads(response.data.data);
      }
    } catch (err) {
      setError('Failed to fetch search payloads');
      console.error('Error fetching search payloads:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setFormData({
      keyword: '',
      merchant: '',
      brand: '',
      isActive: true,
    });
    setSelectedPayload(null);
    setEditDialogOpen(true);
  };

  const handleEdit = (payload: AmazonSearchPayload) => {
    setFormData({
      keyword: payload.keyword,
      merchant: payload.merchant,
      brand: payload.brand,
      isActive: payload.isActive,
    });
    setSelectedPayload(payload);
    setEditDialogOpen(true);
  };

  const handleDelete = (payload: AmazonSearchPayload) => {
    setSelectedPayload(payload);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      if (!formData.keyword.trim()) {
        setError('Keyword is required');
        return;
      }

      if (selectedPayload) {
        // Update
        const response = await api.put(`/amazon-search-payloads/${selectedPayload._id}`, formData);
        if (response.data.success) {
          await fetchPayloads();
          setEditDialogOpen(false);
          setSelectedPayload(null);
        }
      } else {
        // Create
        const response = await api.post('/amazon-search-payloads', formData);
        if (response.data.success) {
          await fetchPayloads();
          setEditDialogOpen(false);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save search payload');
      console.error('Error saving search payload:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedPayload) return;

    try {
      setSaving(true);
      await api.delete(`/amazon-search-payloads/${selectedPayload._id}`);
      await fetchPayloads();
      setDeleteDialogOpen(false);
      setSelectedPayload(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete search payload');
      console.error('Error deleting search payload:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Amazon Search Payloads</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Add Search Payload
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Keyword</TableCell>
              <TableCell>Merchant</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payloads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No search payloads found. Click "Add Search Payload" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              payloads.map((payload) => (
                <TableRow key={payload._id}>
                  <TableCell>{payload.keyword}</TableCell>
                  <TableCell>{payload.merchant}</TableCell>
                  <TableCell>{payload.brand}</TableCell>
                  <TableCell>
                    <Chip
                      label={payload.isActive ? 'Active' : 'Inactive'}
                      color={payload.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(payload.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(payload)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(payload)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedPayload ? 'Edit Search Payload' : 'Create Search Payload'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="Keyword"
              fullWidth
              required
              value={formData.keyword}
              onChange={(e) => setFormData({ ...formData, keyword: e.target.value })}
              margin="normal"
              helperText="Search keywords for Amazon API (e.g., 'cricut accessories DIY sublimation')"
            />
            <TextField
              label="Merchant"
              fullWidth
              value={formData.merchant}
              onChange={(e) => setFormData({ ...formData, merchant: e.target.value })}
              margin="normal"
              helperText="Merchant name (leave empty for all merchants)"
            />
            <TextField
              label="Brand"
              fullWidth
              value={formData.brand}
              onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
              margin="normal"
              helperText="Brand name to filter by (leave empty for all brands)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Search Payload</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this search payload?
          </Typography>
          {selectedPayload && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2"><strong>Keyword:</strong> {selectedPayload.keyword}</Typography>
              <Typography variant="body2"><strong>Merchant:</strong> {selectedPayload.merchant}</Typography>
              <Typography variant="body2"><strong>Brand:</strong> {selectedPayload.brand}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

